import React, { useEffect, useMemo, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCheckbox,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
  MDBTextArea,
} from "mdb-react-ui-kit";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import {
  deleteDebugLogRule,
  deleteDebugLogs,
  getDebugLogRules,
  getDebugLogs,
  upsertDebugLogRule,
} from "Projects/CC_cubi_clan_project/Utils/IncidentService";
import "./IncidentLogsPanel.css";

const APP_OPTIONS = ["", "SSO", "TEAN", "CCON", "TABL", "HAPK", "BOX", "GMF", "MFM", "CUBE"];
const LEVEL_OPTIONS = ["", "SEVERE", "WARNING", "INFO", "CONFIG", "FINE", "FINER", "FINEST"];
const RULE_LEVEL_OPTIONS = [
  { name: "", value: "" },
  { name: "SEVERE", value: 1000 },
  { name: "WARNING", value: 900 },
  { name: "INFO", value: 800 },
  { name: "CONFIG", value: 700 },
  { name: "FINE", value: 500 },
  { name: "FINER", value: 400 },
  { name: "FINEST", value: 300 },
];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("es-ES", { hour12: false });
}

function toDatetimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const EMPTY_RULE = {
  idRule: null,
  name: "",
  enabled: true,
  sourceProject: "",
  classNamePattern: "",
  methodNamePattern: "",
  userId: "",
  userName: "",
  minLevelName: "",
  messageContains: "",
  fromDate: "",
  toDate: "",
  priority: "0",
  notes: "",
};

export default function IncidentLogsPanel() {
  const [activeView, setActiveView] = useState("rules");

  const [rulesLoading, setRulesLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [ruleFilters, setRuleFilters] = useState({ enabled: "", sourceProject: "", nameContains: "" });
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(EMPTY_RULE);

  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [selectedLogIds, setSelectedLogIds] = useState(new Set());
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(50);
  const [logsTotalRows, setLogsTotalRows] = useState(0);
  const [logsTotalPages, setLogsTotalPages] = useState(0);
  const [logFilters, setLogFilters] = useState({
    sourceProject: "",
    className: "",
    methodName: "",
    userId: "",
    userName: "",
    levelName: "",
    ruleId: "",
    message: "",
    fromDate: "",
    toDate: "",
  });

  const enabledFilterParsed = useMemo(() => {
    if (ruleFilters.enabled === "") return undefined;
    return ruleFilters.enabled === "1";
  }, [ruleFilters.enabled]);

  const resolveRuleLevelName = (rule) => {
    const fromName = String(rule?.minLevelName || "").trim().toUpperCase();
    if (fromName) return fromName;
    const numeric = Number(rule?.minLevelValue);
    if (Number.isNaN(numeric)) return "";
    const found = RULE_LEVEL_OPTIONS.find((level) => level.value === numeric);
    return found?.name || "";
  };

  const loadRules = async () => {
    setRulesLoading(true);
    try {
      const data = await getDebugLogRules({
        enabled: enabledFilterParsed,
        sourceProject: ruleFilters.sourceProject || undefined,
        nameContains: ruleFilters.nameContains || undefined,
      });
      setRules(data);
    } catch (error) {
      toast.error(error?.message || "No se pudieron cargar las reglas de logs");
    } finally {
      setRulesLoading(false);
    }
  };

  const loadLogs = async (targetPage = logsPage, filters = logFilters) => {
    setLogsLoading(true);
    try {
      const result = await getDebugLogs({
        page: targetPage,
        pageSize: logsPageSize,
        sourceProject: filters.sourceProject || undefined,
        className: filters.className || undefined,
        methodName: filters.methodName || undefined,
        userId: filters.userId || undefined,
        userName: filters.userName || undefined,
        levelName: filters.levelName || undefined,
        ruleId: filters.ruleId || undefined,
        message: filters.message || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
      });
      setLogs(result.rows || []);
      setSelectedLogIds(new Set());
      setLogsPage(result.page || targetPage);
      setLogsTotalRows(result.totalRows || 0);
      setLogsTotalPages(result.totalPages || 0);
    } catch (error) {
      toast.error(error?.message || "No se pudieron cargar los logs");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeView === "logs") {
      loadLogs(1, logFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, logsPageSize]);

  const openNewRuleModal = () => {
    setRuleDraft(EMPTY_RULE);
    setShowRuleModal(true);
  };

  const openEditRuleModal = (rule) => {
    setRuleDraft({
      idRule: rule?.idRule ?? null,
      name: rule?.name || "",
      enabled: !!rule?.enabled,
      sourceProject: rule?.sourceProject || "",
      classNamePattern: rule?.classNamePattern || "",
      methodNamePattern: rule?.methodNamePattern || "",
      userId: rule?.userId ?? "",
      userName: rule?.userName || "",
      minLevelName: resolveRuleLevelName(rule),
      messageContains: rule?.messageContains || "",
      fromDate: toDatetimeLocal(rule?.fromDate),
      toDate: toDatetimeLocal(rule?.toDate),
      priority: rule?.priority ?? 0,
      notes: rule?.notes || "",
    });
    setShowRuleModal(true);
  };

  const saveRule = async () => {
    const classPattern = String(ruleDraft.classNamePattern || "").trim();
    const methodPattern = String(ruleDraft.methodNamePattern || "").trim();
    const userName = String(ruleDraft.userName || "").trim();
    const hasUserIdText = String(ruleDraft.userId ?? "").trim() !== "";
    const parsedUserId = hasUserIdText ? Number(ruleDraft.userId) : null;

    if (hasUserIdText && (!Number.isInteger(parsedUserId) || parsedUserId <= 0)) {
      toast.error("Usuario ID debe ser un entero positivo");
      return;
    }

    const hasClass = classPattern !== "";
    const hasMethod = methodPattern !== "";
    const hasUser = hasUserIdText || userName !== "";

    if (hasMethod && !hasClass) {
      toast.error("Para informar método debes informar también clase");
      return;
    }

    if (!hasClass && !hasMethod && !hasUser) {
      toast.error("Debes informar al menos clase o usuario (ID/nombre)");
      return;
    }

    const payload = {
      idRule: ruleDraft.idRule || null,
      name: String(ruleDraft.name || "").trim() || null,
      enabled: !!ruleDraft.enabled,
      sourceProject: ruleDraft.sourceProject || null,
      classNamePattern: hasClass ? classPattern : null,
      methodNamePattern: hasMethod ? methodPattern : null,
      userId: hasUserIdText ? parsedUserId : null,
      // Si vienen ambos, backend y frontend priorizan userId.
      userName: hasUserIdText ? null : (userName || null),
      minLevelName: ruleDraft.minLevelName || null,
      messageContains: ruleDraft.messageContains || null,
      fromDate: ruleDraft.fromDate || null,
      toDate: ruleDraft.toDate || null,
      priority: ruleDraft.priority === "" ? 0 : Number(ruleDraft.priority),
      notes: ruleDraft.notes || null,
    };

    const result = await upsertDebugLogRule(payload);
    if (!result.ok || !["SUCCESS", "NOT_FOUND"].includes(result?.json?.STATE)) {
      toast.error(result?.json?.MESSAGE || "No se pudo guardar la regla");
      return;
    }

    toast.success(result?.json?.MESSAGE || "Regla guardada");
    setShowRuleModal(false);
    loadRules();
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`¿Eliminar la regla '${rule?.name || "sin nombre"}'?`)) return;
    const result = await deleteDebugLogRule(rule?.idRule);
    if (!result.ok || !["SUCCESS", "NOT_FOUND"].includes(result?.json?.STATE)) {
      toast.error(result?.json?.MESSAGE || "No se pudo eliminar la regla");
      return;
    }
    toast.success(result?.json?.MESSAGE || "Regla eliminada");
    loadRules();
  };

  const applyLogFilters = () => {
    setLogsPage(1);
    loadLogs(1, logFilters);
  };

  const resetLogFilters = () => {
    const empty = {
      sourceProject: "",
      className: "",
      methodName: "",
      userId: "",
      userName: "",
      levelName: "",
      ruleId: "",
      message: "",
      fromDate: "",
      toDate: "",
    };
    setLogFilters(empty);
    setLogsPage(1);
    loadLogs(1, empty);
  };

  const goLogsPage = (nextPage) => {
    if (nextPage < 1) return;
    if (logsTotalPages > 0 && nextPage > logsTotalPages) return;
    loadLogs(nextPage, logFilters);
  };

  const toggleLogSelection = (idDebugLog, checked) => {
    setSelectedLogIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(idDebugLog);
      else next.delete(idDebugLog);
      return next;
    });
  };

  const toggleSelectAllVisible = (checked) => {
    if (!checked) {
      setSelectedLogIds(new Set());
      return;
    }
    setSelectedLogIds(new Set((logs || []).map((row) => row.idDebugLog)));
  };

  const deleteSelectedLogs = async () => {
    const ids = Array.from(selectedLogIds || []);
    if (!ids.length) {
      toast.warning("No hay logs seleccionados");
      return;
    }
    if (!window.confirm(`¿Eliminar ${ids.length} logs seleccionados?`)) return;

    const result = await deleteDebugLogs({ selectedIds: ids });
    if (!result.ok || !["SUCCESS", "NOT_FOUND"].includes(result?.json?.STATE)) {
      toast.error(result?.json?.MESSAGE || "No se pudieron eliminar los logs seleccionados");
      return;
    }

    toast.success(`Logs eliminados: ${result?.json?.DATA ?? ids.length}`);
    loadLogs(logsPage, logFilters);
  };

  const deleteFilteredLogs = async () => {
    if (!window.confirm("¿Eliminar todos los logs que cumplan los filtros actuales?") ) return;

    const payload = {
      sourceProject: logFilters.sourceProject || null,
      className: logFilters.className || null,
      methodName: logFilters.methodName || null,
      userId: logFilters.userId || null,
      userName: logFilters.userName || null,
      levelName: logFilters.levelName || null,
      ruleId: logFilters.ruleId || null,
      message: logFilters.message || null,
      fromDate: logFilters.fromDate || null,
      toDate: logFilters.toDate || null,
    };

    const result = await deleteDebugLogs(payload);
    if (!result.ok || !["SUCCESS", "NOT_FOUND"].includes(result?.json?.STATE)) {
      toast.error(result?.json?.MESSAGE || "No se pudieron eliminar los logs filtrados");
      return;
    }

    toast.success(`Logs eliminados: ${result?.json?.DATA ?? 0}`);
    loadLogs(1, logFilters);
  };

  return (
    <MDBCard className="incident-logs-panel">
      <MDBCardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 className="mb-1">Logs de diagnóstico</h5>
          <div className="text-muted small">Gestión de reglas temporales y visualización de logs capturados.</div>
        </div>
        <div className="d-flex gap-2">
          <MDBBtn size="sm" color={activeView === "rules" ? "primary" : "light"} onClick={() => setActiveView("rules")}>
            Configuraciones
          </MDBBtn>
          <MDBBtn size="sm" color={activeView === "logs" ? "primary" : "light"} onClick={() => setActiveView("logs")}>
            Logs generados
          </MDBBtn>
        </div>
      </MDBCardHeader>

      <MDBCardBody>
        {activeView === "rules" && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <select
                className="form-select form-select-sm"
                value={ruleFilters.enabled}
                onChange={(event) => setRuleFilters((prev) => ({ ...prev, enabled: event.target.value }))}
              >
                <option value="">Estado: todos</option>
                <option value="1">Activas</option>
                <option value="0">Inactivas</option>
              </select>
              <select
                className="form-select form-select-sm"
                value={ruleFilters.sourceProject}
                onChange={(event) => setRuleFilters((prev) => ({ ...prev, sourceProject: event.target.value }))}
              >
                <option value="">App: todas</option>
                {APP_OPTIONS.filter(Boolean).map((app) => (
                  <option key={app} value={app}>{app}</option>
                ))}
              </select>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Nombre contiene"
                value={ruleFilters.nameContains}
                onChange={(event) => setRuleFilters((prev) => ({ ...prev, nameContains: event.target.value }))}
              />
              <MDBBtn size="sm" color="primary" onClick={loadRules} disabled={rulesLoading}>Filtrar</MDBBtn>
              <MDBBtn
                size="sm"
                color="light"
                onClick={() => {
                  setRuleFilters({ enabled: "", sourceProject: "", nameContains: "" });
                  setTimeout(() => loadRules(), 0);
                }}
                disabled={rulesLoading}
              >
                Limpiar
              </MDBBtn>
              <MDBBtn size="sm" color="success" onClick={openNewRuleModal}>
                <MDBIcon fas icon="plus" className="me-2" />Nueva regla
              </MDBBtn>
            </div>

            <MDBTable striped hover responsive small>
              <MDBTableHead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Activa</th>
                  <th>App</th>
                  <th>Clase</th>
                  <th>Método</th>
                  <th>Usuario</th>
                  <th>Nivel mínimo</th>
                  <th>Ventana</th>
                  <th>Prioridad</th>
                  <th>Acciones</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {rules.map((rule) => (
                  <tr key={rule.idRule}>
                    <td>{rule.idRule}</td>
                    <td>{rule.name}</td>
                    <td>{rule.enabled ? <MDBBadge color="success">Sí</MDBBadge> : <MDBBadge color="secondary">No</MDBBadge>}</td>
                    <td>{rule.sourceProject || "-"}</td>
                    <td className="text-break" title={rule.classNamePattern || ""}>{rule.classNamePattern || "-"}</td>
                    <td className="text-break" title={rule.methodNamePattern || ""}>{rule.methodNamePattern || "-"}</td>
                    <td>{rule.userId || rule.userName || "-"}</td>
                    <td>{resolveRuleLevelName(rule) || "-"}</td>
                    <td>
                      {!rule.fromDate && !rule.toDate
                        ? "No caduca"
                        : <>{formatDate(rule.fromDate)} <span className="text-muted">→</span> {formatDate(rule.toDate)}</>}
                    </td>
                    <td>{rule.priority ?? 0}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <MDBBtn size="sm" color="primary" outline onClick={() => openEditRuleModal(rule)}>Editar</MDBBtn>
                        <MDBBtn size="sm" color="danger" outline onClick={() => removeRule(rule)}>Eliminar</MDBBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
            {!rules.length && !rulesLoading && <div className="text-muted">No hay reglas para mostrar.</div>}
          </>
        )}

        {activeView === "logs" && (
          <>
            <div className="incident-logs-filters mb-3">
              <div className="d-flex flex-wrap gap-2">
                <select
                  className="form-select form-select-sm"
                  value={logFilters.sourceProject}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, sourceProject: event.target.value }))}
                >
                  <option value="">App: todas</option>
                  {APP_OPTIONS.filter(Boolean).map((app) => (
                    <option key={app} value={app}>{app}</option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm"
                  value={logFilters.levelName}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, levelName: event.target.value }))}
                >
                  <option value="">Nivel: todos</option>
                  {LEVEL_OPTIONS.filter(Boolean).map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Regla ID"
                  value={logFilters.ruleId}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, ruleId: event.target.value }))}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Clase"
                  value={logFilters.className}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, className: event.target.value }))}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Método"
                  value={logFilters.methodName}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, methodName: event.target.value }))}
                />
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Usuario ID"
                  value={logFilters.userId}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, userId: event.target.value }))}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Usuario"
                  value={logFilters.userName}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, userName: event.target.value }))}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Mensaje contiene"
                  value={logFilters.message}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, message: event.target.value }))}
                />
                <input
                  type="datetime-local"
                  className="form-control form-control-sm"
                  value={logFilters.fromDate}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, fromDate: event.target.value }))}
                />
                <input
                  type="datetime-local"
                  className="form-control form-control-sm"
                  value={logFilters.toDate}
                  onChange={(event) => setLogFilters((prev) => ({ ...prev, toDate: event.target.value }))}
                />
              </div>
              <div className="d-flex gap-2 mt-2">
                <MDBBtn size="sm" color="primary" onClick={applyLogFilters} disabled={logsLoading}>Filtrar</MDBBtn>
                <MDBBtn size="sm" color="light" onClick={resetLogFilters} disabled={logsLoading}>Limpiar</MDBBtn>
                <MDBBtn size="sm" color="secondary" outline onClick={() => loadLogs(logsPage, logFilters)} disabled={logsLoading}>
                  <MDBIcon fas icon="rotate" className="me-2" />Actualizar
                </MDBBtn>
                <MDBBtn
                  size="sm"
                  color="danger"
                  outline
                  onClick={deleteSelectedLogs}
                  disabled={logsLoading || selectedLogIds.size === 0}
                >
                  <MDBIcon fas icon="trash" className="me-2" />Borrar seleccionados ({selectedLogIds.size})
                </MDBBtn>
                <MDBBtn size="sm" color="danger" onClick={deleteFilteredLogs} disabled={logsLoading}>
                  <MDBIcon fas icon="eraser" className="me-2" />Borrar filtrados
                </MDBBtn>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <div className="text-muted">
                {logsTotalRows > 0
                  ? `Mostrando ${logs.length} de ${logsTotalRows} logs (página ${logsPage}${logsTotalPages ? `/${logsTotalPages}` : ""})`
                  : "Sin logs"}
              </div>
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="logs-page-size" className="text-muted mb-0">Filas/página</label>
                <select
                  id="logs-page-size"
                  className="form-select form-select-sm"
                  style={{ width: "90px" }}
                  value={logsPageSize}
                  onChange={(event) => {
                    setLogsPageSize(Number(event.target.value) || 50);
                    setLogsPage(1);
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <MDBBtn size="sm" color="light" onClick={() => goLogsPage(logsPage - 1)} disabled={logsLoading || logsPage <= 1}>Anterior</MDBBtn>
                <MDBBtn
                  size="sm"
                  color="light"
                  onClick={() => goLogsPage(logsPage + 1)}
                  disabled={logsLoading || (logsTotalPages > 0 && logsPage >= logsTotalPages)}
                >
                  Siguiente
                </MDBBtn>
              </div>
            </div>

            <MDBTable striped hover responsive small>
              <MDBTableHead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={logs.length > 0 && selectedLogIds.size === logs.length}
                      onChange={(event) => toggleSelectAllVisible(!!event.target.checked)}
                      aria-label="Seleccionar logs visibles"
                    />
                  </th>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Nivel</th>
                  <th>App</th>
                  <th>Clase</th>
                  <th>Método</th>
                  <th>Usuario</th>
                  <th>Regla</th>
                  <th>Mensaje</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {logs.map((row) => (
                  <tr key={row.idDebugLog}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedLogIds.has(row.idDebugLog)}
                        onChange={(event) => toggleLogSelection(row.idDebugLog, !!event.target.checked)}
                        aria-label={`Seleccionar log ${row.idDebugLog}`}
                      />
                    </td>
                    <td>{row.idDebugLog}</td>
                    <td>{formatDate(row.date)}</td>
                    <td><MDBBadge color="dark">{row.levelName || "-"}</MDBBadge></td>
                    <td>{row.sourceProject || "-"}</td>
                    <td className="text-break" title={row.className || ""}>{row.className || "-"}</td>
                    <td className="text-break" title={row.methodName || ""}>{row.methodName || "-"}</td>
                    <td>{row.userId || row.userName || "-"}</td>
                    <td>{row.ruleName ? `${row.ruleName} (#${row.ruleId ?? "-"})` : "-"}</td>
                    <td className="text-break" title={row.message || ""}>{row.message || "-"}</td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
            {!logs.length && !logsLoading && <div className="text-muted">No hay logs para mostrar.</div>}
          </>
        )}
      </MDBCardBody>

      <MDBModal tabIndex="-1" show={showRuleModal} setShow={setShowRuleModal}>
        <MDBModalDialog size="xl" scrollable>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{ruleDraft.idRule ? `Editar regla #${ruleDraft.idRule}` : "Nueva regla de log"}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => setShowRuleModal(false)} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="incident-logs-rule-grid">
                <MDBInput
                  label="Nombre (opcional)"
                  size="sm"
                  placeholder="Se autogenera si se deja vacío"
                  value={ruleDraft.name}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, name: event.target.value }))}
                />
                <div className="d-flex align-items-center">
                  <MDBCheckbox
                    label="Regla activa"
                    checked={!!ruleDraft.enabled}
                    onChange={(event) => setRuleDraft((prev) => ({ ...prev, enabled: !!event.target.checked }))}
                  />
                </div>

                <div>
                  <label className="form-label">Aplicación</label>
                  <select
                    className="form-select form-select-sm"
                    value={ruleDraft.sourceProject}
                    onChange={(event) => setRuleDraft((prev) => ({ ...prev, sourceProject: event.target.value }))}
                  >
                    <option value="">Todas</option>
                    {APP_OPTIONS.filter(Boolean).map((app) => (
                      <option key={app} value={app}>{app}</option>
                    ))}
                  </select>
                </div>

                <MDBInput
                  label="Clase"
                  size="sm"
                  value={ruleDraft.classNamePattern}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, classNamePattern: event.target.value }))}
                />
                <MDBInput
                  label="Método"
                  size="sm"
                  value={ruleDraft.methodNamePattern}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, methodNamePattern: event.target.value }))}
                />
                <MDBInput
                  label="Usuario ID"
                  size="sm"
                  type="number"
                  value={ruleDraft.userId}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, userId: event.target.value }))}
                />
                <MDBInput
                  label="Usuario nombre"
                  size="sm"
                  value={ruleDraft.userName}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, userName: event.target.value }))}
                />
                <div>
                  <label className="form-label">Nivel mínimo</label>
                  <select
                    className="form-select form-select-sm"
                    value={ruleDraft.minLevelName}
                    onChange={(event) => setRuleDraft((prev) => ({ ...prev, minLevelName: event.target.value }))}
                  >
                    <option value="">Sin mínimo</option>
                    {RULE_LEVEL_OPTIONS.filter((level) => level.name).map((level) => (
                      <option key={level.name} value={level.name}>
                        {level.name} ({level.value})
                      </option>
                    ))}
                  </select>
                </div>
                <MDBInput
                  label="Mensaje contiene"
                  size="sm"
                  value={ruleDraft.messageContains}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, messageContains: event.target.value }))}
                />
                <MDBInput
                  label="Desde"
                  size="sm"
                  type="datetime-local"
                  value={ruleDraft.fromDate}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, fromDate: event.target.value }))}
                />
                <MDBInput
                  label="Hasta"
                  size="sm"
                  type="datetime-local"
                  value={ruleDraft.toDate}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, toDate: event.target.value }))}
                />
                <MDBInput
                  label="Prioridad"
                  size="sm"
                  type="number"
                  value={ruleDraft.priority}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, priority: event.target.value }))}
                />
              </div>
              <div className="mt-2">
                <MDBTextArea
                  label="Notas"
                  rows={4}
                  value={ruleDraft.notes}
                  onChange={(event) => setRuleDraft((prev) => ({ ...prev, notes: event.target.value }))}
                />
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="light" onClick={() => setShowRuleModal(false)}>Cancelar</MDBBtn>
              <MDBBtn color="primary" onClick={saveRule}>Guardar</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </MDBCard>
  );
}
